import { useState, useMemo } from 'react';
import { Download, ArrowRightLeft } from 'lucide-react';
import type { ErlangAInputs } from '../types/erlang';
import { calculateErlangA, staffingCurve } from '../utils/erlangEngine';
import { generateErlangAReport, downloadReport } from '../utils/reportGenerator';
import MetricCard from './MetricCard';
import StaffingCurve from './StaffingCurve';
import { InputField } from './ErlangCTab';

export default function ErlangATab() {
  const [inputs, setInputs] = useState<ErlangAInputs>({
    callsPerInterval: 350,
    ahtSeconds: 240,
    intervalMinutes: 30,
    targetSLPercent: 80,
    targetAnswerTime: 20,
    shrinkagePercent: 30,
    costPerAgentHour: 25,
    avgPatienceTime: 120,
  });

  const results = useMemo(() => calculateErlangA(inputs), [inputs]);

  const curveData = useMemo(
    () =>
      staffingCurve(
        results.trafficIntensity,
        inputs.targetAnswerTime,
        inputs.ahtSeconds,
        inputs.targetSLPercent / 100,
        inputs.avgPatienceTime
      ),
    [results.trafficIntensity, inputs.targetAnswerTime, inputs.ahtSeconds, inputs.targetSLPercent, inputs.avgPatienceTime]
  );

  const update = (field: keyof ErlangAInputs, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setInputs((prev) => ({ ...prev, [field]: num }));
    }
  };

  const handleDownload = () => {
    const report = generateErlangAReport(inputs, results);
    downloadReport(report, 'erlang-a-report.txt');
  };

  const occWarning = results.occupancy > 0.88;

  return (
    <div className="space-y-6">
      {/* Comparison Banner */}
      <div className="bg-gradient-to-r from-blue-950/50 to-cyan-950/50 border border-blue-700/30 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <ArrowRightLeft className="text-cyan-400" size={20} />
          <span className="text-sm font-semibold text-white">Erlang C vs Erlang A Comparison</span>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xs text-gray-400">Erlang C Agents</div>
            <div className="text-2xl font-bold text-blue-400">{results.rawAgentsErlangC}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Erlang A Agents</div>
            <div className="text-2xl font-bold text-cyan-400">{results.rawAgents}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">C Overestimates By</div>
            <div className="text-2xl font-bold text-emerald-400">
              {results.agentSavings > 0 ? `${results.agentSavings} agents` : '—'}
            </div>
            {results.agentSavings > 0 && results.rawAgentsErlangC > 0 && (
              <div className="text-xs text-emerald-400/70">
                ({((results.agentSavings / results.rawAgentsErlangC) * 100).toFixed(1)}% fewer with Erlang A)
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inputs */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Inputs</h3>
          <InputField label="Calls per Interval" value={inputs.callsPerInterval} onChange={(v) => update('callsPerInterval', v)} />
          <InputField label="AHT (seconds)" value={inputs.ahtSeconds} onChange={(v) => update('ahtSeconds', v)} />
          <InputField label="Interval Length (min)" value={inputs.intervalMinutes} onChange={(v) => update('intervalMinutes', v)} />
          <InputField label="Target SL %" value={inputs.targetSLPercent} onChange={(v) => update('targetSLPercent', v)} />
          <InputField label="Target Answer Time (s)" value={inputs.targetAnswerTime} onChange={(v) => update('targetAnswerTime', v)} />
          <div className="border border-cyan-500/30 rounded-lg p-3 bg-cyan-500/5">
            <InputField
              label="Avg Patience Time (s)"
              value={inputs.avgPatienceTime}
              onChange={(v) => update('avgPatienceTime', v)}
              tooltip="Average time callers will wait before abandoning. Lower patience = more abandonments = fewer agents needed vs Erlang C. Typical: 60-180s."
            />
          </div>
          <InputField label="Shrinkage %" value={inputs.shrinkagePercent} onChange={(v) => update('shrinkagePercent', v)} />
          <InputField label="Cost per Agent $/hr" value={inputs.costPerAgentHour} onChange={(v) => update('costPerAgentHour', v)} />
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Erlang A Results</h3>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors"
            >
              <Download size={14} /> Download Report
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <MetricCard label="Traffic Intensity" value={results.trafficIntensity.toFixed(2)} sublabel="Erlangs" />
            <MetricCard label="Raw Agents (A)" value={results.rawAgents} highlight />
            <MetricCard label="Rostered Agents" value={results.rosteredAgents} highlight />
            <MetricCard label="Service Level" value={(results.serviceLevel * 100).toFixed(1) + '%'} tooltip="Callers answered within the target time ÷ all calls offered. Callers who hang up count as misses (exact M/M/N+M)." />
            <MetricCard label="ASA" value={results.asa.toFixed(1) + 's'} tooltip="Average wait of callers who are answered (Erlang A). Callers who hang up are excluded." />
            <MetricCard label="P(Wait)" value={(results.probabilityWait * 100).toFixed(1) + '%'} />
            <MetricCard
              label="Occupancy"
              value={(results.occupancy * 100).toFixed(1) + '%'}
              warning={occWarning}
              tooltip="Agent utilization = Traffic Intensity / Agents. A result of your workload and SL target."
            />
            <MetricCard label="Abandon Rate" value={(results.abandonRate * 100).toFixed(2) + '%'} warning={results.abandonRate > 0.05} tooltip="Predicted percentage of callers who will hang up before being answered." />
            <MetricCard label="Calls Abandoned" value={results.callsAbandoned} warning={results.callsAbandoned > 10} tooltip="Estimated number of callers who abandon during this interval." />
            <MetricCard label="Cost / Hour" value={'$' + results.costPerHour.toFixed(2)} />
          </div>

          {occWarning && (
            <div className="text-sm text-cyan-400 bg-cyan-950/30 border border-cyan-700/30 rounded-lg px-4 py-3">
              Occupancy is {(results.occupancy * 100).toFixed(1)}% — above 88% signals burnout risk.
            </div>
          )}

          <StaffingCurve data={curveData} targetSL={inputs.targetSLPercent / 100} showErlangA />
        </div>
      </div>
    </div>
  );
}
